'use client'

import { ArrowUpRightIcon, SearchIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ExchangeIcon } from '@/components/widget/exchange-icon'
import InputWithIcon from '@/components/widget/input-with-icon'
import { rankSpreaderInstrumentCandidates } from '@/features/spreader/lib/spreader-instrument-selection'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { formatSymbol } from '@/lib/utils/format-util'
import { SpreaderMatrixInstrument } from '@/state/widget-instances'
import { Instrument } from '@/types/generated/common/types'

const MAX_RENDERED_INSTRUMENTS = 50

interface SpreaderAssetPickerProps {
  // Instruments already in this row/column list — filtered out so the same instrument can't
  // be added twice.
  existing: SpreaderMatrixInstrument[]
  /** Soft ranking preference only. Cross-ticker Spreader legs remain selectable. */
  preferredBaseCurrency?: string
  onSelect: (instrument: SpreaderMatrixInstrument) => void
  onClose: () => void
  onBrowseAll: () => void
}

// Individual-instrument picker (symbol + exchange), unlike RiskSlidesAssetPicker which
// aggregates by base currency across exchanges — the spread matrix needs "ETH-PERPETUAL on
// Deribit" and "ETH-PERPETUAL on Hyperliquid" as two distinct, separately-addable entries.
export function SpreaderAssetPicker({
  existing,
  preferredBaseCurrency,
  onSelect,
  onClose,
  onBrowseAll,
}: SpreaderAssetPickerProps) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const [query, setQuery] = useState('')
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()

  const existingKeys = useMemo(
    () => new Set(existing.map((i) => `${i.exchange}:${i.symbol}`)),
    [existing]
  )

  const flatInstruments = useMemo(() => {
    const list: { instrument: Instrument; exchange: number }[] = []
    for (const [exchange, instruments] of multiExchangeInstrumentMap) {
      for (const instrument of instruments.values()) {
        if (instrument.isActive === false) continue
        if (existingKeys.has(`${exchange}:${instrument.symbol}`)) continue
        list.push({ instrument, exchange })
      }
    }
    return rankSpreaderInstrumentCandidates(list, preferredBaseCurrency)
  }, [multiExchangeInstrumentMap, existingKeys, preferredBaseCurrency])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? flatInstruments.filter(({ instrument }) => instrument.symbol.toLowerCase().includes(q))
      : flatInstruments
  }, [flatInstruments, query])

  const visible = useMemo(() => matches.slice(0, MAX_RENDERED_INSTRUMENTS), [matches])
  const hiddenCount = matches.length - visible.length

  return (
    <div className="bg-popover text-popover-foreground flex h-80 w-72 flex-col overflow-hidden">
      <InputWithIcon
        className="bg-foreground/2 mb-0 rounded-none border-b"
        inputClassName="rounded-none"
        placeholder="Search instrument"
        icon={<SearchIcon className="size-4" />}
        iconContainerClassName="mr-2"
        autoFocus={canAutoFocusSearch}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        onWheelCapture={(event) => event.stopPropagation()}
      >
        {visible.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center justify-center py-8 text-sm">
            No instruments found
          </div>
        ) : (
          <>
            {visible.map(({ instrument, exchange }) => {
              const iconKey = getExchangeIconKey(exchange)
              return (
                <button
                  key={`${exchange}:${instrument.symbol}`}
                  type="button"
                  className="hover:bg-foreground/5 flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left"
                  onClick={() => {
                    onSelect({ exchange, symbol: instrument.symbol })
                    onClose()
                  }}
                >
                  {iconKey && (
                    <ExchangeIcon exchange={iconKey} className="size-4 shrink-0 rounded-full" />
                  )}
                  <span className="truncate text-xs font-semibold tracking-tight">
                    {formatSymbol(instrument.symbol)}
                  </span>
                </button>
              )
            })}
            {hiddenCount > 0 && (
              <p className="text-muted-foreground px-3 py-2 text-center text-[10px]">
                +{hiddenCount} more — keep typing to narrow
              </p>
            )}
          </>
        )}
      </div>
      <button
        type="button"
        className="text-foreground/60 hover:bg-foreground/5 hover:text-foreground flex shrink-0 items-center justify-center gap-1.5 border-t px-3 py-2 text-xs font-medium"
        onClick={onBrowseAll}
      >
        Browse all markets
        <ArrowUpRightIcon className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}
